// src/pages/PaymentMethodList.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import StripeConnectCard from "../components/StripeConnectCard";
import { formatMoney, formatNumber } from "../utils/formatNumber";
import { A, pillClass } from "../styles/appleClasses";

interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  // FEE-RATE-SEMANTICS-1 Phase 2B: consume the backend-derived effective
  // percent (Phase 1 always populates it), never the raw fee_rate.
  fee_rate_percent: number | null; // effective percentage (e.g., 2.9 == 2.9%)
  fixed_fee: number | null; // currency
  account?: { name: string; type: string };
  account_id?: number;

  // surface fields
  provider?: string | null; // "stripe" | "none" | undefined
  provider_method?: string | null; // "card" | "alipay" | "wechat_pay"
  online?: boolean;
}

function fmtMoney(n: number | null | undefined) {
  // Keeps its own Number.isFinite guard: formatMoney renders Infinity as
  // "$∞", this helper has always rendered the placeholder instead.
  return typeof n === "number" && Number.isFinite(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

function fmtPct(pct: number | null | undefined) {
  // `pct` is already the effective percentage (fee_rate_percent) -- no unit
  // heuristic; the backend is the single source of truth for the value.
  if (!(typeof pct === "number" && Number.isFinite(pct) && pct !== 0)) return null;
  return `${formatNumber(pct)}%`;
}

function feeDisplay(m: PaymentMethod) {
  const parts: string[] = [];
  const pct = fmtPct(m.fee_rate_percent);
  if (pct) parts.push(pct);

  if (
    typeof m.fixed_fee === "number" &&
    Number.isFinite(m.fixed_fee) &&
    m.fixed_fee !== 0
  ) {
    parts.push(fmtMoney(m.fixed_fee));
  }
  return parts.length ? parts.join(" + ") : "—";
}

function providerPill(m: PaymentMethod, onlineLabel: string) {
  if (!m.online) return null;

  const prov = (m.provider || "").toLowerCase();
  const label = prov === "stripe" ? "Stripe" : m.provider ? m.provider : onlineLabel;

  // Semantic “info” pill (standardized) — not brand-blue
  return <span className={`ml-2 ${pillClass("info")}`}>{label}</span>;
}

export default function PaymentMethodList() {
  const { t } = useTranslation();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const fetchMethods = async () => {
    const data = await apiGet<PaymentMethod[]>("/payment-methods");
    setMethods(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredMethods = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return methods;

    return methods.filter((m) => {
      const rateStr =
        typeof m.fee_rate_percent === "number" && Number.isFinite(m.fee_rate_percent)
          ? String(m.fee_rate_percent)
          : "";
      const fixedStr =
        typeof m.fixed_fee === "number" && Number.isFinite(m.fixed_fee)
          ? String(m.fixed_fee)
          : "";

      return (
        (m.name?.toLowerCase() || "").includes(q) ||
        (m.account?.name?.toLowerCase() || "").includes(q) ||
        (m.provider || "").toLowerCase().includes(q) ||
        (m.provider_method || "").toLowerCase().includes(q) ||
        rateStr.includes(q) ||
        fixedStr.includes(q)
      );
    });
  }, [methods, search]);

  // AppleClasses
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  // ✅ Match SalesList / BankAccountList: border on the sticky bar + mb-2 OUTSIDE the border
  const headerBar =
    "sticky top-0 z-30 " +
    "bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 " +
    "border-b border-gray-100 " +
    "px-2 sm:px-6 pt-4 pb-2 mb-2";

  const addBtn = `${A.btnBase} ${A.btnOutline} rounded-2xl`;

  const rowBtn =
    `${A.card} w-full text-left ` +
    "px-5 py-4 flex items-center justify-between gap-4 " +
    "hover:shadow-md hover:border-gray-300 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerBar}>
        <div className="w-full max-w-md mx-auto flex items-center">
          <button
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
            type="button"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h2 className={`flex-1 text-center ${A.title}`}>{t("settings.paymentMethods")}</h2>

          <button
            onClick={() => navigate(companyRoute(company_id!, "/"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-md mx-auto px-2 sm:px-6">
        {/* Stripe section: available to all plans */}
        <StripeConnectCard
          onConnected={fetchMethods}
          onMethodsChanged={fetchMethods}
        />

        {/* Search + Add */}
        <div className="flex items-center justify-between mt-4 mb-4 gap-3">
          <input
            className={`${A.inputBase} flex-1`}
            placeholder={t("paymentMethods.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Link
            to={companyRoute(company_id!, "/settings/payment-methods/new")}
            className={addBtn}
          >
            {t("expenses.categories.add")}
          </Link>
        </div>

        {/* List */}
        <div className="flex flex-col gap-3 pb-10">
          {filteredMethods.length === 0 && (
            <div className="text-gray-400 text-center py-12 text-[13px]">
              {t("paymentMethods.empty")}
            </div>
          )}

          {filteredMethods.map((m) => {
            const prov = (m.provider || "").toLowerCase();
            const title = prov === "stripe" ? "Stripe" : m.name;

            return (
              <button
                key={m.id}
                onClick={() =>
                  navigate(
                    companyRoute(company_id!, `/settings/payment-methods/${m.id}/edit`)
                  )
                }
                className={rowBtn}
                type="button"
              >
                <div className="min-w-0">
                  <div className="flex items-center min-w-0">
                    <div className="truncate text-[13px] font-semibold text-gray-700">
                      {title}
                    </div>
                    {providerPill(m, t("paymentMethods.online"))}
                  </div>
                </div>

                <div className="flex flex-col items-end shrink-0">
                  <div className={`${A.body} ${A.num} font-medium`}>
                    {feeDisplay(m)}
                  </div>
                  {m.account && (
                    <div className={`${A.label} mt-1`}>{m.account.name}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
